import type { Slot, SlotResponseInformation } from '../../types/assertive'
import { stripAYCode } from './adFunctions'
import debug from '../console'

const logger = debug.withTag('utils:adIntegrity')

export const TAKEOVER_EVENT_NAME = 'NtpAdTakeover_Detected'

/** A trusted gesture older than this no longer explains a navigation. */
export const INTENT_WINDOW_MS = 1500

/** Hard cap so a redirect loop can't flood the event stream from one pageview. */
export const MAX_REPORTS_PER_PAGE = 3

const MAX_INTENT_ENTRIES = 20
const MAX_SLOTS = 15
const MAX_URL_LENGTH = 500

export type TakeoverTrigger =
  | 'synthetic-click'
  | 'window-open'
  | 'navigation-api'

export interface UserIntent {
  ts: number
  trusted: boolean
  href: string | null
}

export interface IntentRegistry {
  record(intent: UserIntent): void
  hasRecentTrustedIntent(now: number, windowMs?: number): boolean
}

export function createIntentRegistry(
  maxEntries: number = MAX_INTENT_ENTRIES
): IntentRegistry {
  const intents: UserIntent[] = []
  return {
    record(intent) {
      intents.push(intent)
      if (intents.length > maxEntries) {
        intents.shift()
      }
    },
    hasRecentTrustedIntent(now, windowMs = INTENT_WINDOW_MS) {
      return intents.some(
        (intent) => intent.trusted && now - intent.ts <= windowMs
      )
    },
  }
}

export function isForeignDestination(destination: string): boolean {
  try {
    return (
      new URL(destination, window.location.href).origin !==
      window.location.origin
    )
  } catch {
    return false
  }
}

function truncate(value: string, maxLength: number = MAX_URL_LENGTH): string {
  return value.length > maxLength ? value.slice(0, maxLength) : value
}

export type SlotForensics = {
  placement: string
  adUnitPath: string
  advertiserId: number | null
  campaignId: number | null
  creativeId: number | null
  lineItemId: number | null
  sourceAgnosticCreativeId: number | null
  sourceAgnosticLineItemId: number | null
  isEmpty: boolean
}

/**
 * Snapshot every GPT slot's ad response at the moment a takeover fires.
 * The creative/line-item IDs are the attribution AY reporting cannot provide.
 * Runs only on a trigger, never on the render path.
 */
export function collectSlotForensics(): SlotForensics[] {
  try {
    if (typeof window.googletag?.pubads !== 'function') {
      return []
    }
    const slots: Slot[] = window.googletag.pubads().getSlots() ?? []
    return slots.slice(0, MAX_SLOTS).map((slot) => {
      const info: SlotResponseInformation | null =
        slot.getResponseInformation?.() ?? null
      return {
        placement: truncate(stripAYCode(slot.getSlotElementId())),
        adUnitPath: truncate(slot.getAdUnitPath()),
        advertiserId: info?.advertiserId ?? null,
        campaignId: info?.campaignId ?? null,
        creativeId: info?.creativeId ?? null,
        lineItemId: info?.lineItemId ?? null,
        sourceAgnosticCreativeId: info?.sourceAgnosticCreativeId ?? null,
        sourceAgnosticLineItemId: info?.sourceAgnosticLineItemId ?? null,
        isEmpty: info === null,
      }
    })
  } catch (error) {
    logger.warn('Failed to collect slot forensics', error)
    return []
  }
}

export interface TakeoverContext {
  trigger: TakeoverTrigger
  destination: string
  blocked: boolean
  city: string
  country: string
  splitTestName: string
  layoutSkeleton: string
  cohort: string
  pageLoadedAt: number
  now: number
}

export type TakeoverReport = {
  ntp_takeover_trigger: TakeoverTrigger
  ntp_takeover_destination: string
  ntp_takeover_blocked: boolean
  ntp_takeover_page: string
  ntp_takeover_time_on_page_ms: number
  ntp_takeover_visibility: DocumentVisibilityState
  ntp_takeover_slots: SlotForensics[]
  ntp_takeover_city: string
  ntp_takeover_country: string
  split_test_name: string
  layout_skeleton: string
  cohort: string
}

export function buildTakeoverReport(context: TakeoverContext): TakeoverReport {
  return {
    ntp_takeover_trigger: context.trigger,
    ntp_takeover_destination: truncate(context.destination),
    ntp_takeover_blocked: context.blocked,
    ntp_takeover_page: truncate(window.location.href),
    ntp_takeover_time_on_page_ms: context.now - context.pageLoadedAt,
    ntp_takeover_visibility: document.visibilityState,
    ntp_takeover_slots: collectSlotForensics(),
    ntp_takeover_city: context.city,
    ntp_takeover_country: context.country,
    split_test_name: context.splitTestName,
    layout_skeleton: context.layoutSkeleton,
    cohort: context.cohort,
  }
}
